import { TZDateMini } from '@date-fns/tz';
import { format, parse } from 'date-fns';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { checkIfValueTypeAlignment } from './helpers.js';
import { isExpressionString } from '../forms-core/time/timeParser.js';

/**
 * Reads the placeholder text from a spin-button element. The component sets it
 * as a CSS custom property on inline style (`--data-placeholder: "yyyy"`) for
 * the `:empty::before` pseudo-element; we strip the surrounding quotes here.
 */
function getPlaceholder(el: HTMLElement): string {
  const raw = el.style.getPropertyValue('--data-placeholder').trim();
  return raw.startsWith('"') && raw.endsWith('"') ? raw.slice(1, -1) : raw;
}

function sanitizeCopyString(selectionString: string): string {
  return selectionString
    .split('→')
    .map((part) => part.replaceAll('\n', '').trim())
    .join(' ')
    .trim();
}

export function convertCopyString(
  selectionString: string,
  patterns: Array<string> | null,
): string {
  const matchableDateTime: string[] = selectionString
    .split('→')
    .map((dateTime) => dateTime.replace(/\n/g, '').trim());

  const parsedDateTimes: string[] = [];
  // Track whether any segment could not be converted to ISO (locale format or placeholder tokens).
  // When true, segments are joined with '→' to avoid ambiguity with '/' in locale date delimiters.
  let hasNonISO = false;

  for (const [index, dateTimeInput] of matchableDateTime.entries()) {
    // If an expression is detected, use the canonical expression value.
    // Expressions never contain '/' so the standard '/' separator still works.
    if (isExpressionString(dateTimeInput)) {
      parsedDateTimes[index] =
        parseTimeAsTimeValue(dateTimeInput)?.value ?? dateTimeInput;
      continue;
    }

    // Check if the dateTimeInput is parsable by the DtDate
    const currentPattern = patterns?.[index];

    if (
      currentPattern === undefined ||
      (currentPattern.includes('dd') &&
        currentPattern.includes('MM') &&
        currentPattern.includes('yyyy')) === false
    ) {
      // We will need at least day, month and year to create a valid ISO date.
      // Keep the raw locale text and flag it as non-ISO.
      parsedDateTimes[index] = dateTimeInput;
      hasNonISO = true;
      continue;
    }

    try {
      // Try to parse the date with the correct pattern and timezone
      const dateTimeFormat = parse(
        dateTimeInput,
        currentPattern,
        new TZDateMini(0, getTimezone()),
      );

      // Define our precise ISO format, because the removePrecision function moves to a `Z` timezone, while we want to keep the user defined one.
      let precisionPattern = "yyyy-MM-dd'T'HH:mm:ss.SSSXXX";
      if (currentPattern.includes('ss')) {
        precisionPattern = "yyyy-MM-dd'T'HH:mm:ssXXX";
      } else if (currentPattern.includes('mm')) {
        precisionPattern = "yyyy-MM-dd'T'HH:mmXXX";
      } else if (
        currentPattern.includes('dd') &&
        currentPattern.includes('MM') &&
        currentPattern.includes('yyyy')
      ) {
        precisionPattern = 'yyyy-MM-dd';
      }
      parsedDateTimes[index] = format(dateTimeFormat, precisionPattern);
    } catch {
      // Parsing failed (e.g. placeholder tokens like 'MM' in '25/MM/2024').
      // Keep the raw locale text and flag it as non-ISO.
      parsedDateTimes[index] = dateTimeInput;
      hasNonISO = true;
    }
  }

  if (parsedDateTimes.length > 0) {
    // When any segment is non-ISO (locale format or placeholder tokens), use '→' as the
    // separator so that locale date delimiters (e.g. '/') don't create ambiguity.
    // When all segments are ISO, use '/' to preserve the standard timeframe clipboard format.
    const separator = matchableDateTime.length > 1 && hasNonISO ? '→' : '/';
    return parsedDateTimes.filter(Boolean).join(separator);
  }

  return sanitizeCopyString(selectionString);
}

/**
 * Helper function that builds a pattern array from the selected text nodes.
 * Extracts patterns from data-pattern attributes and skips data-expression nodes.
 *
 * Returns the pattern and the raw selected text.
 *
 * Both getRangeAt(0)+intersectsNode and selection.containsNode are unreliable for detecting which nodes are visually
 * selected when the selection spans multiple contenteditable elements. Instead, we use the treeWalker to go over all
 * nodes in the inputgroup, accumulating text and patterns while filtering out nodes that don't align with the
 * value type (e.g. skipping spin button nodes when in expression mode).
 * `contenteditable` needs to stay on the elements to avoid losing the focused element.
 * @internal
 */
export function getPatternAndSelectedText(selection: Selection): {
  pattern: string[] | null;
  text: string;
} {
  if (selection.rangeCount === 0) {
    return { pattern: null, text: '' };
  }

  // Use getRangeAt(0) instead of anchorNode/focusNode + selection.direction.
  // A Range always has startContainer ≤ endContainer in document order, so we
  // never need to guess the direction.
  //
  // Why not selection.direction:
  // Safari reports direction === 'none' for selections created via addRange()
  // when no contenteditable element is active (e.g. after we temporarily strip
  // [contenteditable] to allow Safari's addRange to cross element boundaries).
  // With direction 'none', the old code treated focusNode as the start and
  // anchorNode as the end, so the tree walker would break immediately without
  // capturing any text — producing an empty string on every Cmd+C.
  const range = selection.getRangeAt(0);
  const rawStartNode = range.startContainer;
  const rawEndNode = range.endContainer;

  const root =
    rawStartNode.parentElement?.closest<HTMLDivElement>('.strato-inputgroup');

  if (root === null || root === undefined) {
    return { pattern: null, text: '' };
  }

  const patternParts: Array<string> = [];
  let text = '';
  let capturing = false;
  const treeWalker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return NodeFilter.FILTER_ACCEPT;
        }
        // Empty spin buttons have no text-node child but still display a
        // placeholder via CSS. Visit them so we can include the placeholder
        // in copied selections.
        if (
          node instanceof Element &&
          node.getAttribute('role') === 'spinbutton' &&
          node.childNodes.length === 0
        ) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      },
    },
  );

  let currentNode: Node | null = treeWalker.nextNode();

  // If the endpoint is an element node rather than a text node (e.g. when
  // selectNodeContents(span) was used), step into its first child so the
  // tree-walker identity comparison can match.
  const startNode =
    rawStartNode.nodeType === Node.TEXT_NODE
      ? rawStartNode
      : rawStartNode?.firstChild ?? rawStartNode ?? null;
  const endNode =
    rawEndNode.nodeType === Node.TEXT_NODE
      ? rawEndNode
      : rawEndNode?.firstChild ?? rawEndNode ?? null;

  while (currentNode) {
    if (currentNode === startNode) {
      capturing = true;
    }

    if (capturing && checkIfValueTypeAlignment(currentNode)) {
      if (currentNode.nodeType === Node.TEXT_NODE) {
        text += currentNode.textContent ?? '';
        const parentElement = currentNode.parentElement;
        if (parentElement?.hasAttribute('data-pattern')) {
          patternParts.push(parentElement.getAttribute('data-pattern') ?? '');
        } else if (parentElement?.dataset.valuetype !== 'expression') {
          patternParts.push(currentNode.textContent ?? '');
        }
      } else {
        // Empty spin-button element: contribute its placeholder.
        const el = currentNode as HTMLElement;
        const placeholder = getPlaceholder(el);
        text += placeholder;
        if (el.dataset.pattern !== undefined) {
          patternParts.push(el.dataset.pattern);
        } else if (el.dataset.valuetype !== 'expression') {
          patternParts.push(placeholder);
        }
      }
    }

    if (currentNode === endNode) {
      break;
    }

    currentNode = treeWalker.nextNode();
  }

  const pattern =
    patternParts.length > 0 ? patternParts.join('').split('→') : null;
  return { pattern, text };
}

/**
 * Builds the clipboard text for the current selection by converting the selected
 * spin-button / expression values into a valid timeframe string.
 * Returns null if there is no selection or it does not belong to an inputgroup.
 * @internal
 */
export function buildTimeframeClipboardText(): string | null {
  const selection = window.getSelection();

  if (selection === null || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const root =
    range.startContainer.parentElement?.closest<HTMLDivElement>(
      '.strato-inputgroup',
    );

  if (root === null || root === undefined) {
    return null;
  }

  // Build text and pattern in one DOM walk. selection.toString() in Chrome only returns the text of the first
  // contenteditable element, so we use the range start/end walk in getPatternAndSelectedText.
  const { text: selectionString, pattern } =
    getPatternAndSelectedText(selection);
  return convertCopyString(selectionString, pattern);
}

/**
 * Helper function that tries to parse parts of the current selection as valid time frames
 * and if successful, copies the correctly parsed ISO timestamp or timeframe to the clipboard.
 * Must be called from a native `copy` ClipboardEvent handler so that `clipboardEvent.clipboardData`
 * is available for synchronous writing.
 * @internal
 */
export function onTimeframeCopyCapture(
  clipboardEvent: Pick<ClipboardEvent, 'preventDefault' | 'clipboardData'>,
): void {
  const clipBoardText = buildTimeframeClipboardText();

  if (clipBoardText === null) {
    return;
  }

  // Write synchronously via clipboardEvent.clipboardData and prevent the browser's native copy
  // (which would also only capture the anchor element's text due to contenteditable).
  clipboardEvent.preventDefault();
  clipboardEvent.clipboardData?.setData('text/plain', clipBoardText);
}
